
/**
 * Module dependencies.
 */

import { SpecialPiece } from 'src/types/collection';
import { color, media, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import { normalizeNewLine } from 'src/core/utils/strings';
import { useTranslate } from 'src/core/utils/translator';
import Container from 'src/components/core/layout/container';
import Image from 'src/components/core/image';
import ParallaxFadeInUp from 'src/components/core/animations/parallax-fade-in-up';
import RawHtml from 'src/components/core/raw-html';
import React, { ReactElement } from 'react';
import Type from 'src/components/core/typography';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  description?: string | null,
  isKiosk?: boolean,
  specialPieces: SpecialPiece[] | null
};

/**
 * `Section` styled component.
 */

const Section = styled.section`
  background-color: ${color('beige100')};
  padding: ${units(13)} 0;
  position: relative;

  ${media.min('ms')`
    padding: ${units(25)} 0 ${units(23)};
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min('ms')`
    display: grid;
    grid-template-areas:
      '. title       . list .'
      '. description . list .';
    grid-template-columns: 1fr 3fr 1fr 6fr 1fr;
    grid-template-rows: max-content 1fr;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Type.H2)`
  margin-bottom: ${units(6)};

  ${media.min('ms')`
    grid-area: title;
    grid-row: 1;
  `}
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.div`
  ${media.min('ms')`
    grid-area: description;
    grid-row: 2;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.H6).attrs({
  as: 'p',
  small: true
})`
  margin-bottom: ${units(6)};
`;

/**
 * `List` styled-component.
 */

const List = styled.div`
  display: grid;
  grid-gap: ${units(4)} 1px;
  grid-template-columns: repeat(2, 1fr);

  ${media.min('sm')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('ms')`
    grid-area: list;
    grid-row: 1 / -1;
    grid-template-columns: repeat(4, 1fr);
  `}
`;

/**
 * `ListItem` styled component.
 */

const ListItem = styled.div<{ large: boolean }>`
  ${ifProp('large', css`
    grid-column: auto / span 2;
  `)}
`;

/**
 * `ListItemText` styled component.
 */

const ListItemText = styled(Type.Small).attrs({ as: 'p' })`
  color: ${color('brown300')};
  padding: 0 ${units(1)};
`;

/**
 * `SpecialPiecesSection` component.
 */

const SpecialPiecesSection = (props: Props): ReactElement | null => {
  const { description, isKiosk, specialPieces } = props;
  const { translate } = useTranslate();
  const isMobile = useBreakpoint('ms', 'max');

  if (isEmpty(specialPieces)) {
    return null;
  }

  return (
    <Section>
      <Container hasPadding={!isKiosk}>
        <Grid>
          <Title>
            <ParallaxFadeInUp parallaxOptions={{ speed: 1 }}>
              {translate('collections:details.specialPieces.title')}
            </ParallaxFadeInUp>
          </Title>

          <DescriptionWrapper>
            <ParallaxFadeInUp parallaxOptions={{ speed: 2 }}>
              <RawHtml element={Description}>
                {description ? normalizeNewLine(description) : translate('collections:details.specialPieces.description')}
              </RawHtml>
            </ParallaxFadeInUp>
          </DescriptionWrapper>

          <List>
            {specialPieces.map(({ description, id, large, media, title }) => (
              <ParallaxFadeInUp
                key={id}
                parallaxOptions={{ speed: isMobile ? 2 : -1 }}
              >
                <ListItem large={large}>
                  <Image
                    alt={media[0]?.customProperties?.title ? media[0]?.customProperties?.title : title}
                    height={136}
                    layout={'intrinsic'}
                    src={media[0]?.fileUrl}
                    width={177}
                  />

                  <ListItemText as={'h5'}>
                    {title}
                  </ListItemText>

                  <RawHtml element={ListItemText}>
                    {normalizeNewLine(description)}
                  </RawHtml>
                </ListItem>
              </ParallaxFadeInUp>
            ))}
          </List>
        </Grid>
      </Container>
    </Section>
  );
};

/**
 * Export `SpecialPiecesSection` component.
 */

export default SpecialPiecesSection;
